/**
 * heat-pump-config.js
 * Stepper form: pool dimensions → volume → required kW → filter heat pumps.
 *
 * Power formula:
 *   P (kW) = Volume (m³) × ΔT (°C) × 1.162 / hours × safety_factor
 *
 *   1.162 = specific heat of water expressed in kWh / (m³·°C)
 *           = 4.186 kJ/(kg·°C) × 1000 kg/m³ / 3600 s/h
 *   safety_factor = 1.20 (20% overhead for outdoor heat loss)
 */

(function () {
    'use strict';

    var SPECIFIC_HEAT_KWH_M3 = 1.162;
    var STORAGE_KEY_PREFIX = 'heat-pump-config';
    var PERSISTED_STATE_KEYS = [
        'shape',
        'length',
        'width',
        'diameter',
        'surface',
        'depth',
        'temp_current',
        'temp_desired',
        'temp_air',
        'climate_country',
        'climate_region',
        'climate_month',
        'has_cover',
        'heating_hours',
        'step'
    ];

    document.querySelectorAll('[data-hpc-root]').forEach(function (root, rootIndex) {

        /* ---- DOM references ---- */
        var stepBtns = root.querySelectorAll('[data-hpc-step-btn]');
        var panels = root.querySelectorAll('[data-hpc-panel]');
        var nextBtns = root.querySelectorAll('[data-hpc-next]');
        var prevBtns = root.querySelectorAll('[data-hpc-prev]');
        var shapeInputs = root.querySelectorAll('[data-hpc-shape]');

        var volumePreview = root.querySelector('[data-hpc-volume-preview]');
        var volumeDisplay = root.querySelector('[data-hpc-volume-display]');
        var powerDisplay = root.querySelector('[data-hpc-power-display]');
        var summaryEl = root.querySelector('[data-hpc-summary]');
        var productsEl = root.querySelector('[data-hpc-products]');
        var noResultsEl = root.querySelector('[data-hpc-no-results]');
        var productCards = root.querySelectorAll('[data-hpc-product]');
        var climateWrap = root.querySelector('[data-hpc-climate]');
        var climateTableHead = root.querySelector('[data-hpc-climate-table-head]');
        var climateTableBody = root.querySelector('[data-hpc-climate-table-body]');
        var climateStatus = root.querySelector('[data-hpc-climate-status]');
        var climateDataUrl = climateWrap ? climateWrap.getAttribute('data-hpc-climate-url') : '';
        var climateCountryFilter = climateWrap ? (climateWrap.getAttribute('data-hpc-climate-country') || '').toLowerCase() : '';
        var climateAutoText = root.querySelector('[data-hpc-climate-auto]');
        var storageKey = STORAGE_KEY_PREFIX + ':' + window.location.pathname + ':' + rootIndex;
        var climateRows = [];

        function decodeHtmlEntities(value) {
            if (!value) return value;
            var parser = document.createElement('textarea');
            parser.innerHTML = value;
            return parser.value;
        }

        function getI18n(attrName, fallback) {
            var value = root.getAttribute(attrName);
            if (value && value.trim()) return decodeHtmlEntities(value);
            return fallback;
        }

        function formatMessage(template, replacements) {
            return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, function (_, token) {
                return Object.prototype.hasOwnProperty.call(replacements, token) ? replacements[token] : '';
            });
        }

        function readFactor(attrName, fallback) {
            var raw = root.getAttribute(attrName);
            var parsed = parseFloat(raw);
            return isNaN(parsed) ? fallback : parsed;
        }

        var multiplierConfig = {
            safetyFactor: readFactor('data-hpc-safety-factor', 1.20),
            coverFactor: readFactor('data-hpc-cover-factor', 0.85),
            airFreezing: readFactor('data-hpc-air-factor-freezing', 1.30),
            airCold: readFactor('data-hpc-air-factor-cold', 1.18),
            airMild: readFactor('data-hpc-air-factor-mild', 1.08),
            airWarm: readFactor('data-hpc-air-factor-warm', 1.00),
            airHot: readFactor('data-hpc-air-factor-hot', 0.95)
        };

        /* ---- State ---- */
        var state = {
            step: 1,
            shape: 'rectangular',
            length: null,
            width: null,
            diameter: null,
            surface: null,
            depth: null,
            volume: 0,
            temp_current: 15,
            temp_desired: 28,
            temp_air: 20,
            climate_country: '',
            climate_region: '',
            climate_month: '',
            has_cover: false,
            heating_hours: 24,
            power_kw: 0
        };

        function getInputStateValue(input) {
            if (input.type === 'checkbox') return input.checked;
            if (input.tagName === 'SELECT') return input.value || '';
            var numeric = parseFloat(input.value);
            return isNaN(numeric) ? null : numeric;
        }

        function setInputElementValue(input, value) {
            if (input.type === 'checkbox') {
                input.checked = value === true;
                return;
            }
            if (input.tagName === 'SELECT') {
                input.value = value || '';
                return;
            }
            input.value = value === null || typeof value === 'undefined' ? '' : value;
        }

        function updateSliderValue(key) {
            var valueEl = root.querySelector('[data-hpc-live-value="' + key + '"]');
            if (!valueEl) return;

            var value = state[key];
            if (value === null || typeof value === 'undefined') {
                valueEl.textContent = '—';
                return;
            }

            var unit = valueEl.getAttribute('data-hpc-unit') || '°C';
            valueEl.textContent = value + unit;
        }

        function updateAllSliderValues() {
            updateSliderValue('temp_current');
            updateSliderValue('temp_desired');
            updateSliderValue('temp_air');
            updateSliderValue('heating_hours');
        }

        function updateCoverLabel() {
            var coverLabel = root.querySelector('[data-hpc-live-cover]');
            if (!coverLabel) return;
            coverLabel.textContent = state.has_cover
                ? getI18n('data-hpc-i18n-cover-yes', 'Yes')
                : getI18n('data-hpc-i18n-cover-no', 'No');
        }

        function toTitleCase(value) {
            if (!value) return '';
            return value.charAt(0).toUpperCase() + value.slice(1);
        }

        function rangeText(range) {
            if (!range || typeof range.min === 'undefined' || typeof range.max === 'undefined') return '-';
            return range.min + ' ' + getI18n('data-hpc-i18n-range-separator', 'to') + ' ' + range.max + '°C';
        }

        function translateClimateMonth(monthKey) {
            if (!monthKey) return '';

            var normalizedMonthKey = String(monthKey).toLowerCase();
            var translatedMonth = getI18n('data-hpc-i18n-month-' + normalizedMonthKey, '');

            return translatedMonth || toTitleCase(normalizedMonthKey);
        }

        function getClimateCountries(rows) {
            if (!rows || !rows.length) return [];
            var countries = Object.keys(rows[0]).filter(function (key) {
                return key !== 'month';
            });

            if (!climateCountryFilter) return countries;

            return countries.filter(function (country) {
                return country.toLowerCase() === climateCountryFilter;
            });
        }

        function renderClimateTable(rows) {
            if (!climateTableBody) return;

            var countries = getClimateCountries(rows);
            var areaHeaders = [
                {
                    key: 'north',
                    label: getI18n('data-hpc-i18n-area-north', 'North')
                },
                {
                    key: 'center',
                    label: getI18n('data-hpc-i18n-area-center', 'Center')
                },
                {
                    key: 'south',
                    label: getI18n('data-hpc-i18n-area-south', 'South')
                }
            ];

            if (climateTableHead) {
                var areaHeaderRow = '<tr><th scope="col">' + getI18n('data-hpc-i18n-month', 'Month') + '</th>' +
                    areaHeaders.map(function (area) {
                        return '<th scope="col">' + area.label + '</th>';
                    }).join('') +
                    '</tr>';

                climateTableHead.innerHTML = areaHeaderRow;
            }

            if (!rows || !rows.length) {
                var emptyColspan = (countries.length * 3) + 1;
                climateTableBody.innerHTML =
                    '<tr><td colspan="' + emptyColspan + '" class="hpc-climate-table__status">' +
                    getI18n('data-hpc-i18n-climate-no-data', 'No climate data available.') +
                    '</td></tr>';
                return;
            }

            var html = rows.map(function (row) {
                var countryCells = countries.map(function (country) {
                    var countryData = row[country] || {};
                    return areaHeaders.map(function (area) {
                        return '<td>' + rangeText(countryData[area.key]) + '</td>';
                    }).join('');
                }).join('');

                return (
                    '<tr>' +
                    '<td>' + translateClimateMonth(row.month) + '</td>' +
                    countryCells +
                    '</tr>'
                );
            }).join('');

            climateTableBody.innerHTML = html;
        }

        function setSelectOptions(selectEl, values, selectedValue) {
            if (!selectEl) return;

            var placeholder = selectEl.querySelector('option[value=""]');
            var placeholderHtml = placeholder
                ? placeholder.outerHTML
                : '<option value="">' + getI18n('data-hpc-i18n-select-placeholder', 'Select') + '</option>';
            var optionsHtml = values.map(function (value) {
                return '<option value="' + value + '">' + toTitleCase(value) + '</option>';
            }).join('');

            selectEl.innerHTML = placeholderHtml + optionsHtml;

            if (selectedValue && values.indexOf(selectedValue) !== -1) {
                selectEl.value = selectedValue;
            } else if (!selectedValue && values.length) {
                selectEl.value = values[0];
            }
        }

        function applyClimateSelection() {
            var month = state.climate_month;
            var country = state.climate_country;
            var region = state.climate_region;

            if (!month || !country || !region || !climateRows.length) return;

            var row = climateRows.find(function (item) {
                return item.month === month;
            });

            var regionData = row && row[country] ? row[country][region] : null;
            if (!regionData || typeof regionData.min === 'undefined' || typeof regionData.max === 'undefined') return;

            var avg = Math.round((regionData.min + regionData.max) / 2);
            var airInput = root.querySelector('[data-hpc-input="temp_air"]');

            if (airInput) airInput.value = avg;
            state.temp_air = avg;

            updateSliderValue('temp_air');
            updatePowerPreview();

            if (climateAutoText) {
                climateAutoText.textContent = formatMessage(
                    getI18n(
                        'data-hpc-i18n-climate-auto',
                        'Auto-set external air temp to {{ temp }}°C ({{ country }} {{ region }}, {{ month }}: {{ min }} to {{ max }}°C).'
                    ),
                    {
                        temp: avg,
                        country: toTitleCase(country),
                        region: toTitleCase(region),
                        month: translateClimateMonth(month),
                        min: regionData.min,
                        max: regionData.max
                    }
                );
            }

            persistState();
        }

        function initClimateSelectors(rows) {
            var countrySelect = root.querySelector('[data-hpc-climate-country]');
            var regionSelect = root.querySelector('[data-hpc-climate-region]');
            var monthSelect = root.querySelector('[data-hpc-climate-month]');

            if (!countrySelect || !regionSelect || !monthSelect || !rows.length) return;

            var months = rows.map(function (row) {
                return row.month;
            });

            var firstRow = rows[0];
            var countries = getClimateCountries(rows);

            if (climateCountryFilter && countries.indexOf(climateCountryFilter) !== -1) {
                state.climate_country = climateCountryFilter;
            }

            var defaultCountry = state.climate_country && countries.indexOf(state.climate_country) !== -1
                ? state.climate_country
                : (countries[0] || '');

            var regions = defaultCountry && firstRow[defaultCountry]
                ? Object.keys(firstRow[defaultCountry])
                : [];

            setSelectOptions(countrySelect, countries, state.climate_country);
            state.climate_country = countrySelect.value || '';

            setSelectOptions(regionSelect, regions, state.climate_region);
            state.climate_region = regionSelect.value || '';

            setSelectOptions(monthSelect, months, state.climate_month);
            state.climate_month = monthSelect.value || '';

            applyClimateSelection();
        }

        function refreshClimateRegionOptions() {
            var regionSelect = root.querySelector('[data-hpc-climate-region]');
            if (!regionSelect || !climateRows.length || !state.climate_country) return;

            var firstRow = climateRows[0];
            var regions = firstRow[state.climate_country] ? Object.keys(firstRow[state.climate_country]) : [];
            setSelectOptions(regionSelect, regions, state.climate_region);
            state.climate_region = regionSelect.value || '';
        }

        function loadClimateTable() {
            if (!climateDataUrl || !climateTableBody) return;

            fetch(climateDataUrl)
                .then(function (response) {
                    if (!response.ok) throw new Error('Failed to load climate data');
                    return response.json();
                })
                .then(function (rows) {
                    climateRows = Array.isArray(rows) ? rows : [];
                    if (climateCountryFilter) {
                        state.climate_country = climateCountryFilter;
                    }

                    renderClimateTable(climateRows);
                    initClimateSelectors(climateRows);
                })
                .catch(function () {
                    var fallbackCountries = getClimateCountries(climateRows);
                    var fallbackColspan = ((fallbackCountries.length || (climateCountryFilter ? 1 : 0)) * 3) + 1;
                    if (climateStatus) {
                        climateStatus.textContent = getI18n('data-hpc-i18n-climate-unavailable', 'Unable to load climate data.');
                    } else if (climateTableBody) {
                        climateTableBody.innerHTML =
                            '<tr><td colspan="' + fallbackColspan + '" class="hpc-climate-table__status">' +
                            getI18n('data-hpc-i18n-climate-unavailable', 'Unable to load climate data.') +
                            '</td></tr>';
                    }
                });
        }

        function persistState() {
            try {
                var persisted = {};

                PERSISTED_STATE_KEYS.forEach(function (key) {
                    persisted[key] = state[key];
                });

                window.localStorage.setItem(storageKey, JSON.stringify(persisted));
            } catch (error) {
                // Ignore storage failures such as disabled localStorage.
            }
        }

        function restoreState() {
            try {
                var rawState = window.localStorage.getItem(storageKey);
                if (!rawState) return;

                var persisted = JSON.parse(rawState);
                if (!persisted || typeof persisted !== 'object') return;

                if (typeof persisted.shape === 'string') {
                    state.shape = persisted.shape;

                    shapeInputs.forEach(function (input) {
                        input.checked = input.value === persisted.shape;
                    });
                }

                root.querySelectorAll('[data-hpc-input]').forEach(function (input) {
                    var key = input.dataset.hpcInput;
                    if (!Object.prototype.hasOwnProperty.call(persisted, key)) return;

                    var value = persisted[key];
                    setInputElementValue(input, value);
                    if (input.tagName === 'SELECT') {
                        state[key] = typeof value === 'string' ? value : '';
                    } else {
                        state[key] = getInputStateValue(input);
                    }
                });

                updateAllSliderValues();
                updateCoverLabel();

                if (typeof persisted.step === 'number' && persisted.step >= 1 && persisted.step <= 3) {
                    state.step = persisted.step;
                }
            } catch (error) {
                // Ignore invalid or unavailable stored state.
            }
        }

        /* ================================================================
           Shape selector
           ================================================================ */
        shapeInputs.forEach(function (input) {
            input.addEventListener('change', function () {
                state.shape = input.value;
                updateShapeFields();
                updateVolumePreview();
                persistState();
            });
        });

        function updateShapeFields() {
            root.querySelectorAll('[data-hpc-shape-fields]').forEach(function (group) {
                var shapes = group.dataset.hpcShapeFields.split(' ');
                var visible = shapes.indexOf(state.shape) !== -1;
                group.classList.toggle('is-hidden', !visible);
            });
        }

        /* ================================================================
           Live input sync
           ================================================================ */
        root.querySelectorAll('[data-hpc-input]').forEach(function (input) {
            // Seed state from current input values (e.g. default values in HTML)
            var key = input.dataset.hpcInput;
            state[key] = getInputStateValue(input);

            var eventName = (input.type === 'checkbox' || input.tagName === 'SELECT') ? 'change' : 'input';
            input.addEventListener(eventName, function () {
                state[key] = getInputStateValue(input);

                var isDim = ['length', 'width', 'diameter', 'surface', 'depth'].indexOf(key) !== -1;
                var isTemp = ['temp_current', 'temp_desired', 'temp_air', 'heating_hours', 'has_cover'].indexOf(key) !== -1;

                if (isDim) updateVolumePreview();
                if (isTemp) updatePowerPreview();

                if (key === 'climate_country' || key === 'climate_region' || key === 'climate_month') {
                    if (key === 'climate_country') {
                        refreshClimateRegionOptions();
                    }
                    applyClimateSelection();
                }

                updateSliderValue(key);
                updateCoverLabel();

                persistState();
            });
        });

        /* ================================================================
           Calculations
           ================================================================ */
        function calcVolume() {
            var depth = state.depth;
            if (!depth || depth <= 0) return 0;

            if (state.shape === 'rectangular') {
                var l = state.length, w = state.width;
                if (!l || !w || l <= 0 || w <= 0) return 0;
                return l * w * depth;
            }

            if (state.shape === 'oval') {
                var lo = state.length, wo = state.width;
                if (!lo || !wo || lo <= 0 || wo <= 0) return 0;
                return (Math.PI / 4) * lo * wo * depth;
            }

            if (state.shape === 'circular') {
                var d = state.diameter;
                if (!d || d <= 0) return 0;
                return (Math.PI / 4) * d * d * depth;
            }

            if (state.shape === 'custom') {
                var surface = state.surface;
                if (!surface || surface <= 0) return 0;
                return surface * depth;
            }

            return 0;
        }

        function getAirTempFactor() {
            var tempAir = state.temp_air;
            if (tempAir === null || typeof tempAir === 'undefined' || isNaN(tempAir)) return 1;
            if (tempAir <= 0) return multiplierConfig.airFreezing;
            if (tempAir <= 10) return multiplierConfig.airCold;
            if (tempAir <= 20) return multiplierConfig.airMild;
            if (tempAir <= 30) return multiplierConfig.airWarm;
            return multiplierConfig.airHot;
        }

        function getCoverFactor() {
            return state.has_cover ? multiplierConfig.coverFactor : 1.00;
        }

        function calcPower(volume) {
            var dT = (state.temp_desired || 0) - (state.temp_current || 0);
            var hours = state.heating_hours || 24;
            if (dT <= 0 || hours <= 0 || volume <= 0) return 0;

            var basePower = volume * dT * SPECIFIC_HEAT_KWH_M3 / hours * multiplierConfig.safetyFactor;
            return basePower * getAirTempFactor() * getCoverFactor();
        }

        function updateVolumePreview() {
            var vol = calcVolume();
            state.volume = vol;

            if (volumePreview) volumePreview.hidden = (vol <= 0);
            if (volumeDisplay) {
                volumeDisplay.textContent = vol > 0
                    ? vol.toFixed(1) + ' m³ (' + Math.round(vol * 1000) + ' L)'
                    : '—';
            }

            updatePowerPreview();
        }

        function updatePowerPreview() {
            var vol = state.volume > 0 ? state.volume : calcVolume();
            var kw = calcPower(vol);
            state.power_kw = kw;

            if (powerDisplay) {
                powerDisplay.textContent = kw > 0 ? kw.toFixed(1) + ' kW' : '—';
            }
        }

        function createSummaryItem(label, value, itemClass) {
            var item = document.createElement('li');
            item.className = 'hpc-summary-item ' + itemClass;

            var labelEl = document.createElement('span');
            labelEl.className = 'hpc-summary-item__label';
            labelEl.textContent = label;

            var valueEl = document.createElement('strong');
            valueEl.className = 'hpc-summary-item__value';
            valueEl.textContent = value;

            item.appendChild(labelEl);
            item.appendChild(valueEl);
            return item;
        }

        function renderSummary(vol, needed) {
            if (!summaryEl) return;

            var list = document.createElement('ul');
            list.className = 'hpc-summary-list';

            list.appendChild(createSummaryItem(
                getI18n('data-hpc-i18n-summary-pool-volume-label', 'Pool volume'),
                vol.toFixed(1) + ' m³',
                'hpc-summary-item--volume'
            ));

            list.appendChild(createSummaryItem(
                getI18n('data-hpc-i18n-summary-required-power-label', 'Required power'),
                getI18n('data-hpc-i18n-summary-required-power-prefix', '>=') + ' ' + needed.toFixed(1) + ' kW',
                'hpc-summary-item--power'
            ));

            list.appendChild(createSummaryItem(
                getI18n('data-hpc-i18n-summary-air-factor-label', 'Air factor'),
                'x' + getAirTempFactor().toFixed(2),
                'hpc-summary-item--air-factor'
            ));

            list.appendChild(createSummaryItem(
                getI18n('data-hpc-i18n-summary-cover-factor-label', 'Cover factor'),
                'x' + getCoverFactor().toFixed(2),
                'hpc-summary-item--cover-factor'
            ));

            summaryEl.textContent = '';
            summaryEl.appendChild(list);
        }

        /* ================================================================
           Step navigation
           ================================================================ */
        function goToStep(step) {
            state.step = step;

            panels.forEach(function (p) {
                var n = parseInt(p.dataset.hpcPanel, 10);
                p.classList.toggle('is-active', n === step);
            });

            stepBtns.forEach(function (btn) {
                var n = parseInt(btn.dataset.hpcStepBtn, 10);
                btn.classList.toggle('is-active', n === step);
                btn.classList.toggle('is-complete', n < step);
                btn.disabled = n > step;
                btn.setAttribute('aria-selected', n === step ? 'true' : 'false');
            });

            if (step === 3) filterProducts();

            persistState();
        }

        /* ================================================================
           Validation
           ================================================================ */
        function validateStep1() {
            var required;

            if (state.shape === 'circular') {
                required = ['diameter', 'depth'];
            } else if (state.shape === 'custom') {
                required = ['surface', 'depth'];
            } else {
                required = ['length', 'width', 'depth'];
            }

            var valid = true;

            required.forEach(function (key) {
                var el = root.querySelector('[data-hpc-input="' + key + '"]');
                if (!el) return;
                var val = parseFloat(el.value);
                if (isNaN(val) || val <= 0) {
                    el.classList.add('is-invalid');
                    valid = false;
                } else {
                    el.classList.remove('is-invalid');
                }
            });

            return valid;
        }

        function validateStep2() {
            var valid = true;

            ['temp_current', 'temp_desired', 'temp_air', 'heating_hours'].forEach(function (key) {
                var el = root.querySelector('[data-hpc-input="' + key + '"]');
                if (!el) return;
                var val = parseFloat(el.value);
                if (isNaN(val)) {
                    el.classList.add('is-invalid');
                    valid = false;
                } else {
                    el.classList.remove('is-invalid');
                }
            });

            var dT = (state.temp_desired || 0) - (state.temp_current || 0);
            if (dT <= 0) {
                var desiredEl = root.querySelector('[data-hpc-input="temp_desired"]');
                if (desiredEl) desiredEl.classList.add('is-invalid');
                valid = false;
            }

            return valid;
        }

        /* ================================================================
           Next / Prev buttons
           ================================================================ */
        nextBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
                var from = parseInt(btn.dataset.hpcNext, 10);
                if (from === 1 && !validateStep1()) return;
                if (from === 2 && !validateStep2()) return;
                goToStep(from + 1);
            });
        });

        prevBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
                var from = parseInt(btn.dataset.hpcPrev, 10);
                goToStep(from - 1);
            });
        });

        // Allow clicking completed step indicators to navigate back
        stepBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
                var target = parseInt(btn.dataset.hpcStepBtn, 10);
                if (!btn.disabled && target < state.step) {
                    goToStep(target);
                }
            });
        });

        /* ================================================================
           Product filtering (Step 3)
           ================================================================ */
        function filterProducts() {
            var vol = state.volume > 0 ? state.volume : calcVolume();
            var needed = calcPower(vol);
            state.power_kw = needed;

            // Update summary line
            renderSummary(vol, needed);

            var visibleCount = 0;
            var cards = root.querySelectorAll('[data-hpc-product]');

            cards.forEach(function (card) {
                var maxKw = parseFloat(card.dataset.maxKw || card.dataset.kw);
                var maxVolume = parseFloat(card.dataset.maxVolume || card.dataset.volume);



                if (isNaN(maxKw)) maxKw = 0;
                if (isNaN(maxVolume)) maxVolume = 0;

                var matches = false;
                matches = (maxKw >= needed && maxVolume >= vol)

                // console.table([
                //     {
                //         type: 'kw',
                //         needed,
                //         available: maxKw,
                //         ok: maxKw >= needed
                //     },
                //     {
                //         type: 'volume',
                //         needed: vol,
                //         available: maxVolume,
                //         ok: maxVolume >= vol
                //     },
                //     {
                //         type: 'matches',
                //         needed: '',
                //         available: '',
                //         ok: matches
                //     },
                // ]);


                card.classList.toggle('is-hidden', !matches);
                if (matches) visibleCount++;
            });

            if (noResultsEl) noResultsEl.hidden = visibleCount > 0;
            if (productsEl) productsEl.style.display = visibleCount > 0 ? '' : 'none';
        }

        /* ================================================================
           Init
           ================================================================ */
        restoreState();
        loadClimateTable();
        updateShapeFields();
        updateVolumePreview();
        updateAllSliderValues();
        updateCoverLabel();
        goToStep(state.step);
    });

})();
